import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { api } from '../services/api';
import { useCompanyId } from './CompanyContext';
import { TERMS_VERSION } from '../constants/terms';

interface TermsAcceptanceStatus {
  acceptedAt: string | null;
  version: string | null;
}

// Acceptance is gated in the UI at each go-live (StepGoLive,
// ExperimentDashboard) and enforced by the server on start. Any new go-live
// surface must add the same isTermsAccepted → TermsAcceptanceModal gate.
interface TermsContextValue {
  isTermsAccepted: boolean;
  acceptTerms: () => Promise<void>;
}

const TermsContext = createContext<TermsContextValue | null>(null);

export function useTerms(): TermsContextValue {
  const context = useContext(TermsContext);
  if (!context) {
    throw new Error('useTerms must be used within TermsProvider');
  }
  return context;
}

interface TermsProviderProps {
  children: ReactNode;
  initialStatus?: TermsAcceptanceStatus;
}

export function TermsProvider({ children, initialStatus }: TermsProviderProps) {
  const companyId = useCompanyId();
  const [status, setStatus] = useState<TermsAcceptanceStatus>(
    initialStatus || { acceptedAt: null, version: null }
  );

  const isTermsAccepted = !!status.acceptedAt && status.version === TERMS_VERSION;

  const acceptTerms = useCallback(async () => {
    const result = await api.acceptTerms(companyId, TERMS_VERSION);
    setStatus({ acceptedAt: result.termsAcceptedAt, version: result.termsVersion });
  }, [companyId]);

  return (
    <TermsContext.Provider value={{ isTermsAccepted, acceptTerms }}>
      {children}
    </TermsContext.Provider>
  );
}
